import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Users, Building2, Wallet, UserPlus, Trash2, ChevronDown, ChevronUp, Download, Upload, Filter, X, LogOut, RefreshCw, FileUp, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Label mappings for display
const STAGE_LABELS = {
  'idea': 'Idea/Pre-product',
  'mvp': 'MVP built',
  'traction': 'Early traction',
  'scaling': 'Scaling'
};

const CAPITAL_LABELS = {
  '<250k': '< USD 250k',
  '250k-500k': 'USD 250k–500k',
  '500k-1m': 'USD 500k–1m',
  '1m+': 'USD 1m+',
  'undecided': 'Undecided'
};

const CHEQUE_LABELS = {
  '<50k': '< USD 50k',
  '50k-150k': 'USD 50k–150k',
  '150k-500k': 'USD 150k–500k',
  '500k+': 'USD 500k+'
};

const INVESTOR_TYPE_LABELS = {
  'angel': 'Angel',
  'family-office': 'Family Office',
  'vc': 'VC',
  'syndicate': 'Syndicate/Club',
  'other': 'Other'
};

const COFOUNDER_TYPE_LABELS = {
  'technical': 'Technical/Product',
  'commercial': 'Commercial/Growth',
  'operations': 'Operations/Finance'
};

const AVAILABILITY_LABELS = {
  'fulltime': 'Full-time',
  'parttime': 'Part-time',
  'advisory': 'Advisory'
};

export default function WaitlistAdmin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
    intent: '',
    founderStage: '',
    capitalRange: '',
    chequeSize: ''
  });
  const [activeCardFilter, setActiveCardFilter] = useState(null);
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Selection state for bulk actions
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const getToken = () => localStorage.getItem('admin_token');
  
  const logout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: username === 'admin' ? 'admin@seedsynch.com' : username,
        password: password
      });
      
      // Check if user is admin
      if (response.data.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        setLoginLoading(false);
        return;
      }
      
      localStorage.setItem('admin_token', response.data.access_token);
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        axios.get(`${API}/waitlist/admin/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/waitlist/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSubmissions(subsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (token) {
      setIsAuthenticated(true);
      fetchData();
    } else {
      setLoading(false);
    }
  }, [fetchData]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleDelete = async (id, email, role) => {
    const token = getToken();
    try {
      await axios.delete(`${API}/waitlist/admin/submission/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete submission');
    }
  };

  const handleExport = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API}/waitlist/admin/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download CSV
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Failed to export data');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportLoading(true);
    setImportResult(null);
    
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/waitlist/admin/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} records`);
        fetchData(); // Refresh the data
      } else {
        toast.warning('No records were imported');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import data');
      setImportResult({ error: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImportLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadSampleCSV = () => {
    const sampleCSV = `full_name,email,role,company_name,startup_stage,capital_range,cheque_size,intent_note
John Founder,john@example.com,Founder,TechStartup Inc,mvp,250k-500k,,Looking for seed investors
Jane Investor,jane@example.com,Investor,,,150k-500k,Interested in fintech startups
Bob CoFounder,bob@example.com,Co-founder,,,,,Technical co-founder seeking opportunities`;
    
    const blob = new Blob([sampleCSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'waitlist_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Sample template downloaded');
  };

  // Bulk selection handlers
  const toggleRowSelection = (id) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === filteredSubmissions.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredSubmissions.map(s => s.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    
    setBulkDeleteLoading(true);
    const token = getToken();
    let deleted = 0;
    let failed = 0;
    
    for (const id of selectedRows) {
      try {
        await axios.delete(`${API}/waitlist/admin/submission/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch (err) {
        failed++;
      }
    }
    
    setBulkDeleteLoading(false);
    setShowBulkDeleteDialog(false);
    setSelectedRows(new Set());
    
    if (deleted > 0) {
      toast.success(`Deleted ${deleted} record${deleted > 1 ? 's' : ''}`);
      fetchData();
    }
    if (failed > 0) {
      toast.error(`Failed to delete ${failed} record${failed > 1 ? 's' : ''}`);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCardClick = (cardType) => {
    if (activeCardFilter === cardType) {
      // Clear filter
      setActiveCardFilter(null);
      setFilters(prev => ({ ...prev, role: '' }));
    } else {
      setActiveCardFilter(cardType);
      if (cardType === 'total') {
        setFilters(prev => ({ ...prev, role: '' }));
      } else if (cardType === 'founders') {
        setFilters(prev => ({ ...prev, role: 'Founder' }));
      } else if (cardType === 'cofounders') {
        setFilters(prev => ({ ...prev, role: 'Co-founder' }));
      } else if (cardType === 'investors') {
        setFilters(prev => ({ ...prev, role: 'Investor' }));
      }
    }
  };

  const clearFilters = () => {
    setFilters({ role: '', intent: '', founderStage: '', capitalRange: '', chequeSize: '' });
    setActiveCardFilter(null);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  // Filter submissions
  const filteredSubmissions = submissions.filter(s => {
    // Role filter - handle "all" as no filter
    if (filters.role && filters.role !== 'all' && s.role !== filters.role) return false;
    
    // Intent filter
    if (filters.intent && filters.intent !== 'all') {
      const intent = deriveIntent(s).toLowerCase();
      if (!intent.includes(filters.intent.toLowerCase())) return false;
    }
    
    // Founder stage filter
    if (filters.founderStage && filters.founderStage !== 'all') {
      if (s.role !== 'Founder' || s.startup_stage !== filters.founderStage) return false;
    }
    
    // Capital range filter
    if (filters.capitalRange && filters.capitalRange !== 'all') {
      if (s.role !== 'Founder' || s.capital_range !== filters.capitalRange) return false;
    }
    
    // Cheque size filter
    if (filters.chequeSize && filters.chequeSize !== 'all') {
      if (s.role !== 'Investor' || s.cheque_size !== filters.chequeSize) return false;
    }
    
    return true;
  });

  function deriveIntent(s) {
    if (s.role === 'Founder') {
      const lf = s.founder_looking_for || [];
      if (lf.includes('both')) return 'Both';
      if (lf.includes('capital')) return 'Capital';
      if (lf.includes('cofounder')) return 'Co-founder';
    } else if (s.role === 'Co-founder') {
      const lf = s.cofounder_looking_for || [];
      if (lf.includes('joining')) return 'Joining startup';
      if (lf.includes('partnering')) return 'Partnering';
      if (lf.includes('open')) return 'Open';
    } else if (s.role === 'Investor') {
      const interests = s.investor_interest || [];
      if (interests.includes('early-stage')) return 'Early-stage';
      if (interests.includes('cofounder-matching')) return 'Co-founder matching';
      if (interests.includes('equity-opportunities')) return 'Equity opportunities';
    }
    return '-';
  }

  function getStatusBadge(s) {
    if (s.role === 'Founder') {
      if (s.capital_range && s.capital_range !== 'undecided') {
        return { text: 'Actionable', color: 'bg-green-500/20 text-green-400' };
      }
      if (s.capital_range === 'undecided') {
        return { text: 'Exploratory', color: 'bg-yellow-500/20 text-yellow-400' };
      }
    } else if (s.role === 'Investor') {
      if (s.cheque_size === '150k-500k' || s.cheque_size === '500k+') {
        return { text: 'High capacity', color: 'bg-blue-500/20 text-blue-400' };
      }
      if (s.cheque_size) {
        return { text: 'Actionable', color: 'bg-green-500/20 text-green-400' };
      }
    } else if (s.role === 'Co-founder') {
      if (s.cofounder_skillset?.length > 0) {
        return { text: 'Actionable', color: 'bg-green-500/20 text-green-400' };
      }
    }
    
    // Check for missing key fields (legacy)
    const hasLegacyFields = s.startup_stage_legacy || s.geography_base || s.organization_name;
    if (hasLegacyFields && !s.founder_looking_for?.length && !s.investor_interest?.length) {
      return { text: 'Legacy', color: 'bg-gray-500/20 text-gray-400' };
    }
    
    return { text: 'Partial', color: 'bg-gray-500/20 text-gray-400' };
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 p-8 w-full max-w-md relative">
          {/* Back Home Link */}
          <Link 
            to="/" 
            className="absolute top-4 left-4 flex items-center gap-1.5 text-slate-400 hover:text-blue-400 transition-colors text-sm"
            data-testid="back-home-link"
          >
            <ArrowLeft size={16} />
            Back Home
          </Link>
          
          <div className="text-center mb-8 mt-4">
            <h1 className="text-2xl font-bold text-white mb-2">Waitlist Admin</h1>
            <p className="text-slate-400 text-sm">Sign in to manage waitlist signups</p>
          </div>
          
          <form onSubmit={login} className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder="Username or email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="admin-username"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="admin-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loginLoading}
              data-testid="admin-login-btn"
            >
              {loginLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4 sm:p-6" data-testid="waitlist-admin-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Waitlist Dashboard</h1>
            <p className="text-slate-400 text-sm">Manage SeedSynch waitlist signups</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchData}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw size={16} className="mr-1" />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={logout}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <LogOut size={16} className="mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto mb-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total */}
          <Card 
            className={`bg-slate-800 border-slate-700 p-5 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeCardFilter === 'total' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleCardClick('total')}
            data-testid="card-total"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Users className="text-blue-400" size={20} />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Total Signups</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
            </div>
            {activeCardFilter === 'total' && <p className="text-blue-400 text-xs">Showing all</p>}
          </Card>

          {/* Founders */}
          <Card 
            className={`bg-slate-800 border-slate-700 p-5 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeCardFilter === 'founders' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleCardClick('founders')}
            data-testid="card-founders"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Building2 className="text-green-400" size={20} />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Founders</p>
                <p className="text-2xl font-bold text-white">{stats.founders}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="text-slate-500">Capital: {stats.founder_seeking_capital}</span>
              <span className="text-slate-500">CoF: {stats.founder_seeking_cofounder}</span>
            </div>
            {activeCardFilter === 'founders' && <p className="text-blue-400 text-xs mt-2">Active filter</p>}
          </Card>

          {/* Co-founders */}
          <Card 
            className={`bg-slate-800 border-slate-700 p-5 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeCardFilter === 'cofounders' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleCardClick('cofounders')}
            data-testid="card-cofounders"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <UserPlus className="text-purple-400" size={20} />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Co-founders</p>
                <p className="text-2xl font-bold text-white">{stats.cofounders}</p>
              </div>
            </div>
            {activeCardFilter === 'cofounders' && <p className="text-blue-400 text-xs mt-2">Active filter</p>}
          </Card>

          {/* Investors */}
          <Card 
            className={`bg-slate-800 border-slate-700 p-5 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg ${activeCardFilter === 'investors' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleCardClick('investors')}
            data-testid="card-investors"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Wallet className="text-amber-400" size={20} />
              </div>
              <div>
                <p className="text-slate-400 text-xs">Investors</p>
                <p className="text-2xl font-bold text-white">{stats.investors}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="text-slate-500">&lt;$50k: {stats.investor_cheque_under_50k}</span>
              <span className="text-slate-500">$50k-150k: {stats.investor_cheque_50k_150k}</span>
              <span className="text-slate-500">$150k+: {stats.investor_cheque_150k_plus}</span>
            </div>
            {activeCardFilter === 'investors' && <p className="text-blue-400 text-xs mt-2">Active filter</p>}
          </Card>
        </div>
      )}

      {/* Filter Bar & Actions */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <Filter size={16} className="mr-1" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <X size={16} className="mr-1" />
                Clear all filters
              </Button>
            )}
            
            <span className="text-slate-400 text-sm">
              Showing {filteredSubmissions.length} of {submissions.length} signups
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Bulk Delete Button - shows when rows are selected */}
            {selectedRows.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDeleteDialog(true)}
                className="bg-red-600 hover:bg-red-700"
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete Selected ({selectedRows.size})
              </Button>
            )}
            <Button
              onClick={() => setShowImportDialog(true)}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="import-btn"
            >
              <Upload size={16} className="mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={handleExport}
              className="btn-primary"
              data-testid="export-btn"
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <AlertDialogContent className="bg-slate-800 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <Trash2 size={20} className="text-red-400" />
                Delete {selectedRows.size} signup{selectedRows.size > 1 ? 's' : ''}?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to delete the selected {selectedRows.size} signup{selectedRows.size > 1 ? 's' : ''}? 
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                disabled={bulkDeleteLoading}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                className="bg-red-600 hover:bg-red-700"
                onClick={handleBulkDelete}
                disabled={bulkDeleteLoading}
              >
                {bulkDeleteLoading ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>Delete {selectedRows.size} signup{selectedRows.size > 1 ? 's' : ''}</>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Dialog */}
        <AlertDialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <AlertDialogContent className="bg-slate-800 border-slate-700 max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <FileUp size={20} />
                Import Waitlist Data
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Upload a CSV file to import waitlist signups. The file should contain columns for: 
                <span className="text-slate-300"> full_name, email, role</span> (required), and optionally: 
                <span className="text-slate-300"> company_name, startup_stage, capital_range, cheque_size, intent_note</span>.
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 py-4">
              {/* File input */}
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  id="csv-upload"
                />
                <label 
                  htmlFor="csv-upload"
                  className={`flex items-center justify-center gap-2 w-full py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors
                    ${importLoading 
                      ? 'border-slate-600 bg-slate-700/50 cursor-wait' 
                      : 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/50'
                    }`}
                >
                  {importLoading ? (
                    <>
                      <RefreshCw size={20} className="text-blue-400 animate-spin" />
                      <span className="text-slate-300">Processing...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={20} className="text-slate-400" />
                      <span className="text-slate-300">Click to select CSV file</span>
                    </>
                  )}
                </label>
              </div>
              
              {/* Sample template download */}
              <button 
                onClick={downloadSampleCSV}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
              >
                <Download size={14} />
                Download sample template
              </button>
              
              {/* Import result */}
              {importResult && (
                <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-700/50'}`}>
                  {importResult.error ? (
                    <div className="flex items-start gap-2 text-red-400">
                      <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{importResult.error}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-green-400">
                        <CheckCircle size={18} />
                        <span className="font-medium">{importResult.message}</span>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-slate-400 text-xs mb-2">Issues encountered:</p>
                          <ul className="text-xs text-slate-500 space-y-1 max-h-32 overflow-y-auto">
                            {importResult.errors.map((err, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span className="text-amber-400">•</span>
                                {err}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                onClick={() => {
                  setImportResult(null);
                  setShowImportDialog(false);
                }}
              >
                Close
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <Card className="bg-slate-800 border-slate-700 p-4 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Role</label>
                <Select value={filters.role || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, role: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">All roles</SelectItem>
                    <SelectItem value="Founder" className="text-white">Founder</SelectItem>
                    <SelectItem value="Co-founder" className="text-white">Co-founder</SelectItem>
                    <SelectItem value="Investor" className="text-white">Investor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Intent</label>
                <Select value={filters.intent || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, intent: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">All intents</SelectItem>
                    <SelectItem value="capital" className="text-white">Capital</SelectItem>
                    <SelectItem value="co-founder" className="text-white">Co-founder</SelectItem>
                    <SelectItem value="both" className="text-white">Both</SelectItem>
                    <SelectItem value="joining" className="text-white">Joining startup</SelectItem>
                    <SelectItem value="early-stage" className="text-white">Early-stage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Founder Stage</label>
                <Select value={filters.founderStage || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, founderStage: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">All stages</SelectItem>
                    <SelectItem value="idea" className="text-white">Idea/Pre-product</SelectItem>
                    <SelectItem value="mvp" className="text-white">MVP built</SelectItem>
                    <SelectItem value="traction" className="text-white">Early traction</SelectItem>
                    <SelectItem value="scaling" className="text-white">Scaling</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Capital Range</label>
                <Select value={filters.capitalRange || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, capitalRange: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">All ranges</SelectItem>
                    <SelectItem value="<250k" className="text-white">&lt; USD 250k</SelectItem>
                    <SelectItem value="250k-500k" className="text-white">USD 250k–500k</SelectItem>
                    <SelectItem value="500k-1m" className="text-white">USD 500k–1m</SelectItem>
                    <SelectItem value="1m+" className="text-white">USD 1m+</SelectItem>
                    <SelectItem value="undecided" className="text-white">Undecided</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Cheque Size</label>
                <Select value={filters.chequeSize || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, chequeSize: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="all" className="text-white">All sizes</SelectItem>
                    <SelectItem value="<50k" className="text-white">&lt; USD 50k</SelectItem>
                    <SelectItem value="50k-150k" className="text-white">USD 50k–150k</SelectItem>
                    <SelectItem value="150k-500k" className="text-white">USD 150k–500k</SelectItem>
                    <SelectItem value="500k+" className="text-white">USD 500k+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto">
        <Card className="bg-slate-800 border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No submissions found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400 text-xs font-medium">Name</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium">Email</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium">Role</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium">Intent</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium">Signal</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium">Status</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium">Joined</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium w-20">Actions</TableHead>
                    <TableHead className="text-slate-400 text-xs font-medium w-12 text-center">
                      <Checkbox
                        checked={filteredSubmissions.length > 0 && selectedRows.size === filteredSubmissions.length}
                        onCheckedChange={toggleSelectAll}
                        className="border-slate-500 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubmissions.map((s) => {
                    const status = getStatusBadge(s);
                    const isExpanded = expandedRows[s.id];
                    const isLegacy = !s.founder_looking_for?.length && !s.investor_interest?.length && !s.cofounder_looking_for?.length && (s.startup_stage_legacy || s.geography_base || s.organization_name);
                    
                    return (
                      <>
                        <TableRow 
                          key={s.id} 
                          className="border-slate-700 hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => toggleRow(s.id)}
                        >
                          <TableCell className="text-white font-medium">
                            <div className="flex items-center gap-2">
                              {s.full_name}
                              {isLegacy && (
                                <span className="text-xs bg-gray-500/20 text-gray-400 px-1.5 py-0.5 rounded">Legacy</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300 text-sm">{s.email}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              s.role === 'Founder' ? 'bg-green-500/20 text-green-400' :
                              s.role === 'Co-founder' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {s.role}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-slate-300 text-sm">{deriveIntent(s)}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {s.role === 'Founder' && (
                                <>
                                  {s.startup_stage && (
                                    <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                                      {STAGE_LABELS[s.startup_stage] || s.startup_stage}
                                    </span>
                                  )}
                                  {s.capital_range && (
                                    <span className="text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">
                                      {CAPITAL_LABELS[s.capital_range] || s.capital_range}
                                    </span>
                                  )}
                                </>
                              )}
                              {s.role === 'Investor' && (
                                <>
                                  {s.investor_type && (
                                    <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                                      {INVESTOR_TYPE_LABELS[s.investor_type] || s.investor_type}
                                    </span>
                                  )}
                                  {s.cheque_size && (
                                    <span className="text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">
                                      {CHEQUE_LABELS[s.cheque_size] || s.cheque_size}
                                    </span>
                                  )}
                                </>
                              )}
                              {s.role === 'Co-founder' && (
                                <>
                                  {s.cofounder_skillset?.slice(0, 1).map((sk, i) => (
                                    <span key={i} className="text-xs bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded">
                                      {COFOUNDER_TYPE_LABELS[sk] || sk}
                                    </span>
                                  ))}
                                  {s.cofounder_availability && (
                                    <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                                      {AVAILABILITY_LABELS[s.cofounder_availability] || s.cofounder_availability}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>
                              {status.text}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">{formatDate(s.created_at)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button 
                                className="text-slate-400 hover:text-white transition-colors"
                                onClick={() => toggleRow(s.id)}
                              >
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button className="text-slate-400 hover:text-red-400 transition-colors">
                                    <Trash2 size={16} />
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-slate-800 border-slate-700">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-white">Delete signup?</AlertDialogTitle>
                                    <AlertDialogDescription className="text-slate-400">
                                      Are you sure you want to delete this signup?<br />
                                      <strong className="text-white">{s.email}</strong> ({s.role})<br />
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600">Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      className="bg-red-600 hover:bg-red-700"
                                      onClick={() => handleDelete(s.id, s.email, s.role)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedRows.has(s.id)}
                              onCheckedChange={() => toggleRowSelection(s.id)}
                              className="border-slate-500 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                            />
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Row Details */}
                        {isExpanded && (
                          <TableRow className="border-slate-700 bg-slate-700/30">
                            <TableCell colSpan={9} className="p-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                {s.company_name && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Company</p>
                                    <p className="text-white">{s.company_name}</p>
                                  </div>
                                )}
                                
                                {s.founder_looking_for?.length > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Looking for</p>
                                    <p className="text-white">{s.founder_looking_for.join(', ')}</p>
                                  </div>
                                )}
                                
                                {s.cofounder_type_needed?.length > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Co-founder type needed</p>
                                    <p className="text-white">{s.cofounder_type_needed.map(t => COFOUNDER_TYPE_LABELS[t] || t).join(', ')}</p>
                                  </div>
                                )}
                                
                                {s.cofounder_involvement && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Co-founder involvement</p>
                                    <p className="text-white">{AVAILABILITY_LABELS[s.cofounder_involvement] || s.cofounder_involvement}</p>
                                  </div>
                                )}
                                
                                {s.cofounder_skillset?.length > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Skillset</p>
                                    <p className="text-white">{s.cofounder_skillset.map(sk => COFOUNDER_TYPE_LABELS[sk] || sk).join(', ')}</p>
                                  </div>
                                )}
                                
                                {s.cofounder_looking_for?.length > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Looking for</p>
                                    <p className="text-white">{s.cofounder_looking_for.join(', ')}</p>
                                  </div>
                                )}
                                
                                {s.investor_interest?.length > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Interests</p>
                                    <p className="text-white">{s.investor_interest.join(', ')}</p>
                                  </div>
                                )}
                                
                                {s.intent_note && (
                                  <div className="col-span-2 md:col-span-4">
                                    <p className="text-slate-500 text-xs">Intent note</p>
                                    <p className="text-white">{s.intent_note}</p>
                                  </div>
                                )}
                                
                                {/* Legacy fields */}
                                {s.geography_base && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Geography (legacy)</p>
                                    <p className="text-white">{s.geography_base}</p>
                                  </div>
                                )}
                                {s.organization_name && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Organization (legacy)</p>
                                    <p className="text-white">{s.organization_name}</p>
                                  </div>
                                )}
                                {s.geography_focus && (
                                  <div>
                                    <p className="text-slate-500 text-xs">Geography Focus (legacy)</p>
                                    <p className="text-white">{s.geography_focus}</p>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
